import {CommonModule} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MatBadgeModule} from '@angular/material/badge';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Router} from '@angular/router';
import {
  BusinessProfileService
} from '@app/modules/account-settings/pages/business-profile/services/business-profile.service';
import {ACTIVE_USER} from '@app/modules/auth/providers/auth.provider';
import InAppNotificationLogService from '@app/modules/in-app-notification-log/services/in-app-notification-log.service';
import {User} from '@app/modules/user/models/user.model';
import {AppRoutes, APP_ROUTES} from '@config/app-routes.config';
import {
  AppEventEnum,
} from '@generated/graphql';
import {LetModule} from '@ngrx/component';
import {
  filter,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import {EventSubscription, PusherService} from '@shared/services/pusher.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BrowserNotificationService} from "@shared/services/browser-notification.service";
import {InAppNotificationLog} from "@modules/in-app-notification-log/models/in-app-notification-log.model";
import {SharePipesModule} from "@shared/share-pipes/share-pipes.module";

@UntilDestroy()
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatBadgeModule,
    MatIconModule,
    MatMenuModule,
    LetModule,
    SharePipesModule,
  ],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  subscription: EventSubscription;
  readonly notifications$ = this.notificationService.notifications$;
  readonly unread$ = this.notificationService.unread$
  readonly isDisabledMarkingReadAll$ = this.notificationService.isDisabledMarkingReadAll$

  constructor(
    @Inject(APP_ROUTES) private readonly appRoutes: AppRoutes,
    @Inject(ACTIVE_USER) private readonly activeUser$: Observable<User>,
    private readonly businessProfileService: BusinessProfileService,
    private readonly notificationService: InAppNotificationLogService,
    private readonly router: Router,
    private readonly pusherService: PusherService,
    private readonly browserNotificationService: BrowserNotificationService
  ) {
  }

  ngOnInit(): void {
    this.activeUser$.pipe(
      tap(() => {
        this.notificationService.clearAll()

        this.subscription?.leaveChannel()
      }),
      filter(user => !!user),
      switchMap(user => this.notificationService.get$().pipe(
        tap(() => {
          const channel = this.getChannelName(user.id)

          this.subscription = this.pusherService.subscribe(
            channel,
            AppEventEnum.InAppNotificationCreated,
            payload => this.notificationService.show$(payload.log).subscribe()
          );
        }),
      )),
      untilDestroyed(this)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.subscription?.leaveChannel()
  }

  showDetail(log: InAppNotificationLog) {
    this.notificationService.readThenRedirect$(log).subscribe()
  }

  markReadAll(e: MouseEvent) {
    e.stopPropagation();

    this.notificationService.markReadAll$().subscribe()
  }

  async onNotificationsClick() {
    await this.browserNotificationService.enableNotifications()
  }

  private getChannelName(userId: string) {
    return `App.Models.User.${userId}`
  }
}
